// API Client for Joomla backend communication
import axios, {AxiosError, AxiosInstance} from 'axios';

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  errors?: Record<string, string[]>;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

class ApiClient {
  private appClient: string = 'admin'; // 'site' or 'admin'
  private client: AxiosInstance;
  private baseURL: string;
  private csrfToken: string | null = null;
  private tokenPromise: Promise<string> | null = null;

  constructor() {
    // Get base URL from window object (set by Joomla)
    const joomlaOptions = (window as any).Joomla?.getOptions?.('com_easyforms')
    this.baseURL = joomlaOptions?.apiBase || '/index.php?option=com_easyforms';
    this.appClient = joomlaOptions?.client || 'admin';

    this.client = axios.create({
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      withCredentials: true,
    });

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const apiError = this.handleError(error);
        return Promise.reject(apiError);
      }
    );

    // Initialize token on construction
    this.initializeToken();
  }

  /**
   * Initialize CSRF token on page load
   * This fetches the token once and caches it for all subsequent requests
   */
  private async initializeToken(): Promise<void> {
    try {
      await this.getCSRFToken();
    } catch (error) {
      console.error('Failed to initialize CSRF token:', error);
    }
  }

  /**
   * Get CSRF token with caching
   * Fetches from backend on first call, then returns cached value
   */
  private async getCSRFToken(): Promise<string> {
    // Return cached token if available
    if (this.csrfToken) {
      return this.csrfToken;
    }

    // If token fetch is already in progress, wait for it
    if (this.tokenPromise) {
      return this.tokenPromise;
    }

    // Start token fetch
    this.tokenPromise = this.fetchCSRFToken();

    try {
      this.csrfToken = await this.tokenPromise;
      return this.csrfToken;
    } finally {
      this.tokenPromise = null;
    }
  }

  /**
   * Fetch CSRF token from the backend
   */
  private async fetchCSRFToken(): Promise<string> {
    try {
      // Try to get from Joomla options first (set by ser ver-side)
      const joomlaToken = (window as any).Joomla?.getOptions?.('csrf.token');
      if (joomlaToken) {
        console.log('[API Client] Using CSRF token from Joomla options');
        return joomlaToken;
      }

      // Fetch from backend API
      console.log('[API Client] Fetching CSRF token from backend');
      const response = await this.client.get(`${this.baseURL}&task=api.token`);

      if (response.data?.data?.token) {
        console.log('[API Client] CSRF token fetched successfully');
        return response.data.data.token;
      }

      // Try to get from meta tag
      const metaToken = document.querySelector('meta[name="csrf-token"]');
      if (metaToken?.getAttribute('content')) {
        console.log('[API Client] Using CSRF token from meta tag');
        return metaToken.getAttribute('content')!;
      }

      throw new Error('No CSRF token in response');
    } catch (error) {
      console.error('[API Client] Failed to fetch CSRF token:', error);
      throw new Error('Failed to fetch CSRF token');
    }
  }

  /**
   * Refresh the CSRF token (useful after token expiration)
   */
  public async refreshToken(): Promise<string> {
    console.log('[API Client] Refreshing CSRF token');
    this.csrfToken = null;
    this.tokenPromise = null;
    return this.getCSRFToken();
  }

  private buildProxyUrl(controller: string, task: string): string {
    // Note: CSRF token is added as query parameter in the callProxy method
    const baseUrl = `${this.baseURL}${this.baseURL.includes('?') ? '&' : '?'}`

    return this.appClient === 'admin' ? `${baseUrl}task=${controller}.${task}&format=json` : `${baseUrl}&task=api.proxy&action=${controller}.${task}&format=json`;
  }

  private handleError(error: AxiosError): ApiError {
    if (error.response) {
      // Server responded with error
      const data = error.response.data as any;

      // Check if it's a CSRF token error
      if (error.response.status === 403 ||
          data?.message?.toLowerCase().includes('token') ||
          data?.message?.toLowerCase().includes('csrf')) {
        console.warn('[API Client] CSRF token may be invalid, refreshing...');
        // Token might be expired, clear it so it gets refreshed on next request
        this.csrfToken = null;
        this.tokenPromise = null;
      }

      return {
        message: data?.message || 'An error occurred',
        code: data?.code,
        status: error.response.status,
        errors: data?.errors,
      };
    } else if (error.request) {
      // Request made but no response
      return {
        message: 'No response from server',
        code: 'NO_RESPONSE',
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
      };
    }
  }

  /**
   * Call API through proxy pattern
   * @param controller - Controller name (e.g., 'forms', 'submissions', 'answergroups')
   * @param task - Task name (e.g., 'getForms', 'saveForm')
   * @param params - Additional parameters (can be object, URLSearchParams, or FormData)
   * @param method - HTTP method (default: 'POST')
   */
  async callProxy<T = any>(
    controller: string,
    task: string,
    params: any = {},
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'POST'
  ): Promise<T> {
    // Get CSRF token (from cache or fetch if needed)
    const csrfToken = await this.getCSRFToken();

    // Build URL without CSRF token (token is sent in header)
    const url = this.buildProxyUrl(controller, task);

    // Determine request data format
    let requestData: URLSearchParams | FormData | undefined;

    if (method !== 'GET' && params) {
      // Check if params is already FormData
      if (params instanceof FormData) {
        // Use FormData as-is for file uploads
        requestData = params;
      } else {
        // Convert params to URLSearchParams for regular form data
        requestData = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            // Handle arrays and objects
            if (Array.isArray(value) || typeof value === 'object') {
              requestData!.append(key, JSON.stringify(value));
            } else {
              requestData!.append(key, String(value));
            }
          }
        });
      }
    }

    // Prepare headers
    const headers: Record<string, string> = {
      'X-CSRF-TOKEN': csrfToken,
    };

    // Set Content-Type based on request data type
    if (requestData instanceof FormData) {
      // Don't set Content-Type for FormData - let browser set it with boundary
    } else if (method === 'GET') {
      headers['Content-Type'] = 'application/json';
    } else {
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
    }

    const response = await this.client.request({
      url,
      method,
      data: requestData,
      params: method === 'GET' ? params : undefined,
      headers,
    });

    const result = response.data;
    if (!result.success) {
      // If response contains validationErrors, return the data instead of throwing
      // This allows frontend to handle field-specific validation errors gracefully
      if (result.data?.validationErrors) {
        return result.data;
      }

      // For other errors, throw as before
      const error: any = new Error(result.message || 'API request failed');

      // Attach additional error details from response (like request_id)
      if (result.data && typeof result.data === 'object') {
        Object.assign(error, result.data);
      }

      throw error;
    }
    return result.data;
  }

  // Legacy method for backward compatibility - delegates to callProxy
  async get<T = any>(task: string, params?: Record<string, any>): Promise<T> {
    // Parse task to extract controller and actual task
    // Format: "controller.task" or just "task" (defaults to 'api' controller)
    const [controller, actualTask] = task.includes('.')
      ? task.split('.')
      : ['api', task];

    return this.callProxy<T>(controller, actualTask, params, 'GET');
  }

  // Legacy method for backward compatibility - delegates to callProxy
  async post<T = any>(task: string, data?: any): Promise<T> {
    const [controller, actualTask] = task.includes('.')
      ? task.split('.')
      : ['api', task];

    return this.callProxy<T>(controller, actualTask, data, 'POST');
  }

  // Legacy method for backward compatibility - delegates to callProxy
  async put<T = any>(task: string, data?: any): Promise<T> {
    const [controller, actualTask] = task.includes('.')
      ? task.split('.')
      : ['api', task];

    return this.callProxy<T>(controller, actualTask, data, 'PUT');
  }

  // Legacy method for backward compatibility - delegates to callProxy
  async delete<T = any>(task: string, params?: Record<string, any>): Promise<T> {
    const [controller, actualTask] = task.includes('.')
      ? task.split('.')
      : ['api', task];

    return this.callProxy<T>(controller, actualTask, params, 'DELETE');
  }

  // Upload file
  async upload<T = any>(controller: string, task: string, file: File, additionalData?: Record<string, any>): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }

    // Get CSRF token
    const csrfToken = await this.getCSRFToken();
    const url = this.buildProxyUrl(controller, task);

    const response = await this.client.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-CSRF-TOKEN': csrfToken,
      },
    });

    const result = response.data;
    if (!result.success) {
      throw new Error(result.message || 'Upload failed');
    }
    return result.data;
  }

  /**
   * Download a file/blob from API
   * Used for exports and file downloads that return raw data instead of JSON
   * @param controller - Controller name
   * @param task - Task name
   * @param params - Parameters to send
   * @param method - HTTP method (default: GET)
   */
  async downloadBlob(
    controller: string,
    task: string,
    params: any = {},
    method: 'GET' | 'POST' = 'GET'
  ): Promise<Blob> {
    // Get CSRF token
    const csrfToken = await this.getCSRFToken();

    // Build proxy URL - same as callProxy
    const url = this.buildProxyUrl(controller, task);

    // Determine request data format
    let requestData: URLSearchParams | FormData | undefined;

    if (method !== 'GET' && params) {
      // Check if params is already FormData
      if (params instanceof FormData) {
        requestData = params;
      } else {
        // Convert params to URLSearchParams for regular form data
        requestData = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            // Handle arrays and objects
            if (Array.isArray(value) || typeof value === 'object') {
              requestData!.append(key, JSON.stringify(value));
            } else {
              requestData!.append(key, String(value));
            }
          }
        });
      }
    }

    // Prepare headers
    const headers: Record<string, string> = {
      'X-CSRF-TOKEN': csrfToken,
    };

    // Set Content-Type based on request data type
    if (requestData instanceof FormData) {
      // Don't set Content-Type for FormData - let browser set it with boundary
    } else if (method === 'GET') {
      headers['Content-Type'] = 'application/json';
    } else {
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
    }

    const response = await this.client.request({
      url,
      method,
      data: requestData,
      params: method === 'GET' ? params : undefined,
      headers,
      responseType: 'blob', // Important: Tell axios to expect a blob response
    });

    // For blob downloads, the response.data is already the blob
    // No need to check for success/error as blobs don't have that structure
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
